import React, { useState, useMemo } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';

const STOCK_SUGGESTIONS = [
  { ticker: 'AAPL', name: 'Apple Inc.', sector: 'Technology', price: 189.30 },
  { ticker: 'MSFT', name: 'Microsoft Corporation', sector: 'Technology', price: 415.20 },
  { ticker: 'GOOGL', name: 'Alphabet Inc.', sector: 'Technology', price: 172.85 },
  { ticker: 'AMZN', name: 'Amazon.com Inc.', sector: 'Consumer', price: 198.45 },
  { ticker: 'TSLA', name: 'Tesla Inc.', sector: 'Automotive', price: 248.60 },
  { ticker: 'META', name: 'Meta Platforms Inc.', sector: 'Technology', price: 524.10 },
  { ticker: 'NVDA', name: 'NVIDIA Corporation', sector: 'Technology', price: 875.40 },
  { ticker: 'JPM', name: 'JPMorgan Chase & Co.', sector: 'Finance', price: 198.75 },
  { ticker: 'JNJ', name: 'Johnson & Johnson', sector: 'Healthcare', price: 152.30 },
  { ticker: 'V', name: 'Visa Inc.', sector: 'Finance', price: 278.90 },
];

export default function AddStockModal({ onClose, onAdd }) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [shares, setShares] = useState('');
  const [buyPrice, setBuyPrice] = useState('');

  const suggestions = useMemo(() => {
    if (!query?.trim()) return [];
    const q = query?.toLowerCase();
    return STOCK_SUGGESTIONS?.filter(s => s?.ticker?.toLowerCase()?.includes(q) || s?.name?.toLowerCase()?.includes(q))?.slice(0, 5);
  }, [query]);

  const handleSelect = (stock) => {
    setSelected(stock);
    setQuery(stock?.ticker);
    setBuyPrice(stock?.price?.toFixed(2));
  };

  const handleAdd = () => {
    if (!selected || !shares || !buyPrice) return;
    onAdd({ ...selected, shares: parseFloat(shares), avgBuy: parseFloat(buyPrice) });
    onClose();
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(0,48,87,0.7)' }}>
      <div className="w-full max-w-md rounded-2xl shadow-2xl overflow-hidden" style={{ background: 'var(--color-background)' }}>
        <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: 'var(--color-border)', background: 'var(--color-primary)' }}>
          <div className="flex items-center gap-2">
            <Icon name="PlusCircle" size={20} color="var(--color-accent)" />
            <h3 className="font-heading font-semibold text-lg text-white">Add Stock to Portfolio</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors">
            <Icon name="X" size={18} color="white" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="relative">
            <Input label="Search Ticker or Company" type="search" placeholder="e.g. AAPL or Apple"
              value={query} onChange={e => { setQuery(e?.target?.value); setSelected(null); }} />
            {suggestions?.length > 0 && !selected && (
              <div className="absolute top-full left-0 right-0 mt-1 rounded-xl border shadow-lg overflow-hidden z-10"
                style={{ background: 'var(--color-background)', borderColor: 'var(--color-border)' }}>
                {suggestions?.map(s => (
                  <button key={s?.ticker} onClick={() => handleSelect(s)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted transition-colors text-left">
                    <div>
                      <span className="font-data font-bold text-sm" style={{ color: 'var(--color-primary)' }}>{s?.ticker}</span>
                      <span className="font-caption text-xs ml-2" style={{ color: 'var(--color-muted-foreground)' }}>{s?.name}</span>
                    </div>
                    <span className="font-data text-sm font-medium" style={{ color: 'var(--color-foreground)' }}>${s?.price?.toFixed(2)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {selected && (
            <div className="rounded-lg p-3 border" style={{ borderColor: 'rgba(133,113,77,0.3)', background: 'rgba(133,113,77,0.05)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-data font-bold text-sm" style={{ color: 'var(--color-primary)' }}>{selected?.ticker}</p>
                  <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>{selected?.name} · {selected?.sector}</p>
                </div>
                <p className="font-data font-semibold text-sm" style={{ color: 'var(--color-accent)' }}>${selected?.price?.toFixed(2)}</p>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Input label="Number of Shares" type="number" placeholder="0" value={shares} onChange={e => setShares(e?.target?.value)} />
            <Input label="Buy Price ($)" type="number" placeholder="0.00" value={buyPrice} onChange={e => setBuyPrice(e?.target?.value)} />
          </div>
          {shares && buyPrice && (
            <div className="rounded-lg p-3" style={{ background: 'var(--color-muted)' }}>
              <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Total Investment</p>
              <p className="font-data font-bold text-lg" style={{ color: 'var(--color-primary)' }}>
                ${(parseFloat(shares || 0) * parseFloat(buyPrice || 0))?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <Button variant="outline" fullWidth onClick={onClose}>Cancel</Button>
            <Button variant="default" fullWidth onClick={handleAdd} disabled={!selected || !shares || !buyPrice}>Add to Portfolio</Button>
          </div>
        </div>
      </div>
    </div>
  );
}